// src/features/chat/MessageActionsMenu.tsx
// The per-message "more" (⋮) menu, shared between the full chat page
// (ChatDetailPage's MessageItem) and the entity rail panel (PanelMessageList).
// Items appear only when their callback is wired, so a surface opts out of an
// action by simply not passing it (e.g. the panel hides forward/link/tag).
// Edit and delete additionally require `isOwn` — you can only touch your own
// messages.

import { useI18n } from '@react/hooks';
import {
  AnchoredMenu,
  MenuItem,
  MenuSeparator,
  type Anchor,
} from '@/components/AnchoredMenu';
import type { Message } from '@core/types/chat';

export interface MessageActionsMenuProps {
  anchor: Anchor;
  open: boolean;
  onClose: () => void;
  align?: 'start' | 'end';
  message: Message;
  isOwn: boolean;
  /** When true and onUnpin is wired, the pin item becomes Unpin. */
  isPinned?: boolean;
  onCopyLink?: (msg: Message) => void;
  onPin?: (msg: Message) => void;
  onUnpin?: (msg: Message) => void;
  onLink?: (msg: Message) => void;
  onCreateTask?: (msg: Message) => void;
  onTag?: (msg: Message) => void;
  onEdit?: (msg: Message) => void;
  onDelete?: (msg: Message) => void;
}

export function MessageActionsMenu({
  anchor,
  open,
  onClose,
  align = 'start',
  message,
  isOwn,
  isPinned,
  onCopyLink,
  onPin,
  onUnpin,
  onLink,
  onCreateTask,
  onTag,
  onEdit,
  onDelete,
}: MessageActionsMenuProps) {
  const { t } = useI18n();
  const select = (fn: ((msg: Message) => void) | undefined) => () => {
    onClose();
    fn?.(message);
  };
  const showUnpin = isPinned && !!onUnpin;
  const showPin = !showUnpin && !!onPin;
  const showOwnSection = isOwn && (!!onEdit || !!onDelete);

  return (
    <AnchoredMenu
      anchor={anchor}
      open={open}
      onClose={onClose}
      align={align}
      ariaLabel={t('common.actions')}
      className="min-w-44 py-1"
    >
      {/* Copy link to message — clipboard icon to distinguish from the
          chain-link entity-relation item below. */}
      {onCopyLink && (
        <MenuItem
          label={t('chat.copyLink')}
          onSelect={select(onCopyLink)}
          icon={
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
            </svg>
          }
        />
      )}
      {/* Pin/Unpin — thumbtack, distinct from the paperclip used for
          attachments */}
      {(showPin || showUnpin) && (
        <MenuItem
          label={showUnpin ? t('chat.unpin') : t('chat.pinMessage')}
          onSelect={select(showUnpin ? onUnpin : onPin)}
          icon={
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 17v5" />
              <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1Z" />
            </svg>
          }
        />
      )}
      {/* Link to entity (framework-mew) — hidden in contexts where the
          parent didn't wire a picker (e.g. inside a thread sidebar). */}
      {onLink && (
        <MenuItem
          label={t('chat.linkResource')}
          onSelect={select(onLink)}
          icon={
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
            </svg>
          }
        />
      )}
      {/* Create task from this message */}
      {onCreateTask && (
        <MenuItem
          label={t('chat.createTask.menuItem')}
          onSelect={select(onCreateTask)}
          icon={
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          }
        />
      )}
      {/* Tag (framework-zak) */}
      {onTag && (
        <MenuItem
          label={t('chat.tagMessage')}
          onSelect={select(onTag)}
          icon={
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
            </svg>
          }
        />
      )}
      {/* Edit and delete — own messages only */}
      {showOwnSection && <MenuSeparator />}
      {isOwn && onEdit && (
        <MenuItem
          label={t('common.edit')}
          onSelect={select(onEdit)}
          icon={
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          }
        />
      )}
      {isOwn && onDelete && (
        <MenuItem
          label={t('common.delete')}
          destructive
          onSelect={select(onDelete)}
          icon={
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          }
        />
      )}
    </AnchoredMenu>
  );
}
